﻿import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { TFunction } from "i18next";
import { CheckCircle, RefreshCcw } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import type { OrderEnvelope, OrderPayment, PaymentRail, Profile } from "@tilt/pos-sdk";
import { ApiError } from "@tilt/pos-sdk";

import { getPosClient } from "../../lib/api";
import { formatCents } from "../../lib/format";
import { lookupMethodByTypeSubtype, resolveMethodLabel } from "../../lib/payment-methods";
import { cn } from "../../lib/utils";
import { Alert } from "../ui/Alert";
import { Button } from "../ui/Button";
import { Card } from "../ui/Card";
import { Field, TextInput } from "../ui/Field";

export interface RefundPanelProps {
  readonly envelope: OrderEnvelope;
  readonly profile: Profile | undefined;
  readonly locationId?: string | null;
  /** When true the whole panel is dimmed and non-interactive (e.g. payment form is active). */
  readonly locked?: boolean;
  /** Called whenever the refund form opens or closes. */
  readonly onFormActiveChange?: (active: boolean) => void;
  /** Controlled open state - when provided the parent drives the panel;
   *  the closed-state Card is not rendered (parent owns that affordance). */
  readonly open?: boolean;
  readonly onOpenChange?: (open: boolean) => void;
}

export function RefundPanel({
  envelope,
  profile,
  locationId = null,
  locked = false,
  onFormActiveChange,
  open: controlledOpen,
  onOpenChange,
}: RefundPanelProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const order = envelope.order;

  const isControlled = controlledOpen !== undefined;
  const [internalOpen, setInternalOpen] = useState(false);
  const open = isControlled ? (controlledOpen ?? false) : internalOpen;
  function setOpen(val: boolean) {
    if (isControlled) onOpenChange?.(val);
    else setInternalOpen(val);
  }

  const [parentId, setParentId] = useState<string>("");
  const [amount, setAmount] = useState<string>("");
  const [refundSurcharge, setRefundSurcharge] = useState(true);
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [succeeded, setSucceeded] = useState<"refund" | "void" | null>(null);
  const successTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    onFormActiveChange?.(open);
  }, [open, onFormActiveChange]);

  useEffect(() => {
    return () => {
      if (successTimerRef.current) clearTimeout(successTimerRef.current);
    };
  }, []);

  const railsQuery = useQuery({
    enabled: locationId != null,
    queryKey: ["payment-rails", locationId],
    queryFn: () => getPosClient().getLocationPaymentRails(locationId!),
    staleTime: 5 * 60_000,
  });
  const rails = useMemo(() => railsQuery.data?.rails ?? [], [railsQuery.data]);
  const allowCashReturn = railsQuery.data?.allow_cash_return ?? false;

  // Whether the cashier has toggled "Return Cash" for an override situation.
  const [cashReturnChosen, setCashReturnChosen] = useState(false);

  const refundable = useMemo(() => buildRefundable(envelope.payments), [envelope.payments]);
  const canRefund = useMemo(
    () =>
      (profile?.permissions ?? []).some(
        (code) => code === "pos.payment.refund" || code === "pos.payment.refund_own",
      ),
    [profile?.permissions],
  );

  // When there is only one refundable payment, auto-select it so the settlement
  // check fires immediately. With multiple payments, require an explicit selection
  // to avoid firing the API call for the wrong payment.
  const selected =
    refundable.find((r) => r.payment.payment_id === parentId) ??
    (refundable.length === 1 ? refundable[0] : undefined);
  const activeParentId = selected?.payment.payment_id ?? "";

  // Check whether the selected payment can be voided (not yet settled) or
  // must be refunded (already in a closed batch). V1 parity: RefundModal.vue
  // isVoidOperation is driven by the settlement-status endpoint, never by the
  // cashier.
  const selectedPaymentRail = selected
    ? lookupMethodByTypeSubtype(
        rails,
        selected.payment.payment_type,
        selected.payment.payment_subtype ?? null,
      )
    : null;

  // V0116: use reversal_method when available; fall back to is_voidable for
  // rails that pre-date the migration (forward-compat during deploy window).
  const reversalMethod: string =
    selectedPaymentRail?.reversal_method ??
    (selectedPaymentRail?.is_voidable ? "void" : "return_to_original");

  // Only methods that route to the processor need a settlement check.
  const canCheckSettlement =
    open && !!activeParentId && reversalMethod === "return_to_original" && !cashReturnChosen;

  const settlementQuery = useQuery({
    queryKey: ["settlement-status", order.id, activeParentId],
    queryFn: () => getPosClient().getPaymentSettlementStatus(order.id, activeParentId),
    enabled: canCheckSettlement,
    staleTime: 60_000, // re-check after 1 min if drawer is still open
    retry: 1,
  });

  // Void = payment NOT yet settled (is_void_eligible=true).
  // When the check is still loading or failed, default to refund (safe).
  const isVoidOperation =
    reversalMethod === "void" ||
    (canCheckSettlement && settlementQuery.isSuccess && settlementQuery.data.is_void_eligible);

  // Cash-return: method supports it AND location cascade allows it AND cashier opted in.
  const isCashReturn = !!(
    selectedPaymentRail?.supports_cash_return &&
    allowCashReturn &&
    cashReturnChosen
  );

  // Non-refundable: no reversal path exists for this method.
  const isNonRefundable = reversalMethod === "non_refundable";

  const mutation = useMutation({
    mutationFn: async (vars: {
      payment_id: string;
      amount_cents: number;
      surcharge_refund_amount_cents: number;
      has_surcharge: boolean;
      reason: string;
      type: "refund" | "void";
    }) => {
      return getPosClient().refundPayment(order.id, {
        payment_id: vars.payment_id,
        amount_cents: vars.amount_cents,
        // Always send surcharge_refund_amount_cents when the original payment
        // had a surcharge so the API can distinguish "explicitly 0" (cashier
        // opted out) from "not specified" (auto-reverse on void/full-refund).
        ...(vars.has_surcharge
          ? { surcharge_refund_amount_cents: vars.surcharge_refund_amount_cents }
          : {}),
        ...(vars.reason ? { reason: vars.reason } : {}),
        type: vars.type,
      });
    },
    onSuccess: (_data, vars) => {
      void queryClient.invalidateQueries({ queryKey: ["orders", order.id] });
      void queryClient.invalidateQueries({ queryKey: ["orders"] });
      setSucceeded(vars.type === "void" ? "void" : "refund");
      setParentId("");
      setAmount("");
      setRefundSurcharge(true);
      setReason("");
      setError(null);
      setCashReturnChosen(false);
      successTimerRef.current = setTimeout(() => {
        setSucceeded(null);
        setOpen(false);
      }, 2500);
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        const body = err.body as { detail?: string; error?: string } | null;
        setError(body?.detail ?? body?.error ?? t("refund.errorGeneric"));
      } else {
        setError(t("refund.errorGeneric"));
      }
    },
  });

  if (!canRefund) return null;
  if (refundable.length === 0) return null;
  if (!succeeded && (order.status === "voided" || order.status === "refunded")) return null;

  const remainingCents = selected?.remainingCents ?? 0;

  // Void: full amount only - cashier cannot choose a partial amount.
  const amountCentsForVoid = remainingCents;
  const parsedAmount = isVoidOperation
    ? amountCentsForVoid
    : parseAmountCents(amount, remainingCents);

  const originalSurcharge = selected?.payment.surcharge_cents ?? 0;
  const originalCharge = selected?.payment.applied_amount_cents ?? 0;
  // For void, reverse the full surcharge. For partial refund, scale proportionally.
  const surchargeRefundCents =
    originalSurcharge > 0 && refundSurcharge && parsedAmount != null && originalCharge > 0
      ? isVoidOperation
        ? originalSurcharge
        : Math.round((parsedAmount / originalCharge) * originalSurcharge)
      : 0;

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (isNonRefundable) return;
    if (parsedAmount == null) {
      setError(t("refund.invalidAmount"));
      return;
    }
    if (parsedAmount > remainingCents) {
      setError(
        t("refund.exceedsRemaining", {
          max: formatCents(remainingCents, order.currency),
        }),
      );
      return;
    }
    setError(null);
    // Determine reversal type for the API:
    // - void: cash/check path -- cancel in our system only
    // - refund: card/ACH processor refund, or cash-return (no processor call but
    //   recorded as refund in our system so the amount is tracked)
    mutation.mutate({
      payment_id: activeParentId,
      amount_cents: parsedAmount,
      surcharge_refund_amount_cents: surchargeRefundCents,
      has_surcharge: originalSurcharge > 0,
      reason: reason.trim(),
      type: isVoidOperation ? "void" : "refund",
    });
  }

  function handleClose() {
    setOpen(false);
    setError(null);
    setAmount("");
    setReason("");
    setCashReturnChosen(false);
  }

  if (!open) {
    // Controlled mode: parent owns the open affordance - render nothing when closed.
    if (isControlled) return null;
    return (
      <Card>
        <div
          className={cn(
            "flex items-center justify-between gap-4 transition-opacity",
            locked && "pointer-events-none opacity-40",
          )}
        >
          <h2 className="text-sm font-semibold text-foreground">{t("refund.title")}</h2>
          <Button
            variant="secondary"
            leftIcon={<RefreshCcw className="h-4 w-4" />}
            onClick={() => setOpen(true)}
          >
            {t("refund.open")}
          </Button>
        </div>
      </Card>
    );
  }

  // Settlement check is loading.
  const isCheckingSettlement = canCheckSettlement && settlementQuery.isLoading;

  return (
    <div
      className={cn(
        "overflow-hidden rounded-xl border border-brand-primary/20 bg-surface shadow-2xl ring-1 ring-inset ring-brand-primary/10 transition-all dark:shadow-none dark:ring-brand-primary/20",
        locked && "pointer-events-none opacity-40",
      )}
    >
      {/* Panel header */}
      <div className="flex items-center justify-between border-b border-line px-5 py-3">
        <span className="text-xs font-semibold uppercase tracking-wider text-brand-primary">
          {isVoidOperation
            ? t("refund.titleVoid")
            : isCashReturn
              ? t("refund.titleCashReturn", { defaultValue: "Return Cash" })
              : t("refund.title")}
        </span>
        <button
          type="button"
          onClick={handleClose}
          disabled={mutation.isPending}
          className="text-xs font-medium text-muted transition-colors hover:text-foreground disabled:pointer-events-none disabled:opacity-40"
        >
          {t("common.cancel")}
        </button>
      </div>

      {/* Settlement status check in progress */}
      {isCheckingSettlement && (
        <div className="flex flex-col items-center gap-3 px-5 py-7">
          <div className="h-8 w-8 animate-spin rounded-full border-[3px] border-brand-primary/20 border-t-brand-primary" />
          <p className="text-sm font-medium text-foreground">{t("refund.checkingStatus")}</p>
        </div>
      )}

      {/* Spinner while mutation is in flight */}
      {!isCheckingSettlement && mutation.isPending && (
        <div className="flex flex-col items-center gap-3 px-5 py-7">
          <div className="h-8 w-8 animate-spin rounded-full border-[3px] border-brand-primary/20 border-t-brand-primary" />
          <p className="text-sm font-medium text-foreground">
            {isVoidOperation
              ? t("refund.processingVoid")
              : isCashReturn
                ? t("refund.processingCashReturn", { defaultValue: "Recording cash return..." })
                : t("refund.processingRefund")}
          </p>
        </div>
      )}

      {/* Success confirmation - shown briefly before auto-close */}
      {!isCheckingSettlement && !mutation.isPending && succeeded && (
        <div className="flex flex-col items-center gap-3 px-5 py-7">
          <CheckCircle className="h-8 w-8 text-green-500" aria-hidden="true" />
          <p className="text-sm font-semibold text-foreground">
            {succeeded === "void" ? t("refund.successVoid") : t("refund.successRefund")}
          </p>
        </div>
      )}

      {/* Non-refundable banner */}
      {!isCheckingSettlement && !mutation.isPending && !succeeded && isNonRefundable && (
        <div className="px-5 py-6">
          <Alert
            tone="info"
            title={t("refund.nonRefundable.title", { defaultValue: "Not Refundable" })}
          >
            {t("refund.nonRefundable.body", {
              defaultValue: "This payment method cannot be refunded or voided.",
            })}
          </Alert>
        </div>
      )}

      {/* Form - shown once settlement status is known (or for cash/check) */}
      {!isCheckingSettlement && !mutation.isPending && !succeeded && !isNonRefundable && (
        <form onSubmit={handleSubmit} className="space-y-4 p-4">
          {refundable.length > 1 && (
            <Field label={t("refund.fields.parent")}>
              <select
                value={activeParentId}
                onChange={(e) => {
                  setParentId(e.target.value);
                  setAmount("");
                }}
                className="block w-full rounded-md bg-surface px-3 py-2 text-sm text-strong ring-1 ring-inset ring-line focus:outline-none focus:ring-2 focus:ring-inset focus:ring-brand-primary"
              >
                {!activeParentId && (
                  <option value="" disabled>
                    {t("refund.fields.parentPlaceholder")}
                  </option>
                )}
                {refundable.map((r) => (
                  <option key={r.payment.payment_id} value={r.payment.payment_id}>
                    {labelForPayment(r.payment, r.remainingCents, order.currency, t, rails)}
                  </option>
                ))}
              </select>
            </Field>
          )}

          {/* Cash-return toggle: shown when the method supports cash return and the
              location cascade permits it (allow_cash_return in pos_config). */}
          {selectedPaymentRail?.supports_cash_return && allowCashReturn && (
            <label className="flex cursor-pointer items-center gap-2 text-sm text-foreground">
              <input
                type="checkbox"
                checked={cashReturnChosen}
                onChange={(e) => setCashReturnChosen(e.target.checked)}
                className="h-4 w-4 rounded border-line accent-brand-primary"
              />
              {t("refund.cashReturnOverride", {
                defaultValue: "Return cash instead of processor refund",
              })}
            </label>
          )}

          {/* Cash-return info: customer gets cash back, no processor API call. */}
          {isCashReturn && reversalMethod !== "void" && (
            <Alert
              tone="info"
              title={t("refund.cashReturnInfo.title", { defaultValue: "Cash Return" })}
            >
              {t("refund.cashReturnInfo.body", {
                defaultValue:
                  "Hand the customer cash. This will be recorded as a refund in the system.",
              })}
            </Alert>
          )}

          {/* Void mode: full-amount only - no amount input shown (V1 parity). */}
          {isVoidOperation ? (
            <Alert tone="info" title={t("refund.voidInfo.title")}>
              {t("refund.voidInfo.body", {
                amount: formatCents(amountCentsForVoid + surchargeRefundCents, order.currency),
              })}
            </Alert>
          ) : (
            <Field
              label={t("refund.fields.amount")}
              hint={t("refund.fields.amountHint", {
                max: formatCents(remainingCents, order.currency),
              })}
            >
              <TextInput
                inputMode="decimal"
                placeholder={(remainingCents / 100).toFixed(2)}
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                invalid={parsedAmount != null && parsedAmount > remainingCents}
              />
            </Field>
          )}

          {originalSurcharge > 0 && !isVoidOperation && (
            <label className="flex items-center gap-2 text-sm text-foreground">
              <input
                type="checkbox"
                checked={refundSurcharge}
                onChange={(e) => setRefundSurcharge(e.target.checked)}
                className="h-4 w-4 rounded border-line accent-brand-primary"
              />
              {t("refund.fields.refundSurcharge", {
                amount: formatCents(surchargeRefundCents, order.currency),
              })}
            </label>
          )}

          <Field label={t("refund.fields.reason")} hint={t("refund.fields.reasonHint")}>
            <TextInput
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              maxLength={500}
              placeholder={t("refund.fields.reasonPlaceholder")}
            />
          </Field>

          {error && (
            <Alert tone="error" title={t("refund.errorTitle")}>
              {error}
            </Alert>
          )}

          <div className="flex items-center justify-between pt-1">
            <span className="text-sm text-muted" />
            <Button
              type="submit"
              variant="danger"
              loading={mutation.isPending}
              disabled={!activeParentId || parsedAmount == null || parsedAmount <= 0}
            >
              {isVoidOperation
                ? t("refund.submitVoid", {
                    amount: formatCents(amountCentsForVoid + surchargeRefundCents, order.currency),
                  })
                : isCashReturn
                  ? t("refund.submitCashReturn", {
                      amount: formatCents(
                        (parsedAmount ?? remainingCents) + surchargeRefundCents,
                        order.currency,
                      ),
                      defaultValue: "Return Cash {{amount}}",
                    })
                  : t("refund.submit", {
                      amount: formatCents(
                        (parsedAmount ?? remainingCents) + surchargeRefundCents,
                        order.currency,
                      ),
                    })}
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}

interface RefundablePayment {
  readonly payment: OrderPayment;
  readonly remainingCents: number;
}

function buildRefundable(payments: readonly OrderPayment[]): RefundablePayment[] {
  const charges = payments.filter((p) => p.role === "charge" && p.status === "APPROVED");
  const refunds = payments.filter((p) => p.role === "refund");
  // Sum refunds by parent_payment_id (exact attribution). Fall back to payment_type
  // matching for legacy rows that pre-date parent_payment_id being stored server-side.
  const refundedByParent = new Map<string, number>();
  for (const r of refunds) {
    const parentId = r.parent_payment_id ?? null;
    if (parentId) {
      refundedByParent.set(
        parentId,
        (refundedByParent.get(parentId) ?? 0) + r.applied_amount_cents,
      );
    }
  }
  // Legacy fallback: refunds without parent_payment_id - match by type.
  const legacyRefunds = refunds.filter((r) => !r.parent_payment_id);
  const legacyUsed = new Map<string, number>();
  for (const r of legacyRefunds) {
    const candidate = charges.find((c) => c.payment_type === r.payment_type);
    if (candidate) {
      legacyUsed.set(
        candidate.payment_id,
        (legacyUsed.get(candidate.payment_id) ?? 0) + r.applied_amount_cents,
      );
    }
  }
  return charges
    .map((p) => {
      const exactRefunded = refundedByParent.get(p.payment_id) ?? 0;
      const legacyRefunded = legacyUsed.get(p.payment_id) ?? 0;
      const remaining = Math.max(0, p.applied_amount_cents - exactRefunded - legacyRefunded);
      return { payment: p, remainingCents: remaining };
    })
    .filter((r) => r.remainingCents > 0);
}

function labelForPayment(
  p: OrderPayment,
  remainingCents: number,
  currency: string,
  t: TFunction,
  rails: readonly PaymentRail[],
): string {
  const rail = lookupMethodByTypeSubtype(rails, p.payment_type, p.payment_subtype ?? null);
  const method = rail ? resolveMethodLabel(rail, t) : p.payment_type;
  return t("refund.parentLabel", {
    method,
    amount: formatCents(remainingCents, currency),
  });
}

function parseAmountCents(raw: string, fallback: number): number | null {
  const trimmed = raw.trim();
  if (trimmed === "") return fallback;
  if (!/^\d+(\.\d{1,2})?$/.test(trimmed)) return null;
  const dollars = Number(trimmed);
  if (!Number.isFinite(dollars) || dollars <= 0) return null;
  return Math.round(dollars * 100);
}
